/**
 * Income assessment — what a client's assets and income streams actually earn this year, who is taxed on
 * it, and how much of it arrives as cash, as a pure function.
 *
 * One walk, two different questions answered about every dollar, which is why they are answered
 * together:
 *
 *   - the TAX position: each person's assessable income sources, their deductions, and their franking
 *     credits, attributed to whoever owns the asset (split 50/50 when jointly owned). This is what
 *     `calculateFinancials`'s tax calculation is struck on.
 *   - the CASH position: what lands in the bank. It is NOT the same figure. Reinvested share dividends
 *     are taxable but never arrive; franking credits are taxable but are not money at all; an
 *     account-based pension's drawdown is cash but is untaxed once its owner turns 60.
 *
 * The cash side is reported in buckets rather than as one total because a timing-aware consumer needs
 * the parts: the projection engine's incomePlugin pays {@link IncomeAssessment.salaryIncomeCash} and
 * {@link IncomeAssessment.investmentPropertyRentCash} monthly instead of as a 30 June lump, and its
 * expenses counterpart charges {@link IncomeAssessment.investmentPropertyExpenseCash} the same way.
 * Those three are ALSO inside the totals beside them — additive detail, not a separate flow — so a
 * standalone caller can ignore them entirely and still get a correct annual cashflow.
 *
 * Rules worth knowing, each of which the walk encodes per asset type:
 *   - SHARES: the dividend is cash (unless reinvested), but the taxable amount is the dividend GROSSED
 *     UP by its franking credit, which is then claimed back as an offset.
 *   - ACCOUNT-BASED PENSIONS: the payment is the greater of what the client nominated and the
 *     legislated minimum for their age — a pension cannot pay less than the minimum — and it is
 *     tax-free from age 60.
 *   - INVESTMENT PROPERTY: rent is taxable, expenses are deductible, and a negatively geared property
 *     therefore reduces the owner's tax while still costing them cash.
 *   - TERM DEPOSITS and 'other': income is derived from a rate where one is recorded, otherwise implied
 *     from the entered income against the balance.
 *
 * Bank interest and the rent-the-home strategy are assessed before the asset walk, and the
 * trust/company apportionment (`structureAttribution`) is merged in after it — its tax lines are struck
 * by that module, not re-derived here.
 *
 * Extracted from `calculateFinancials`'s "--- 4. Taxable Income & Cash Income Calculation ---" block,
 * which still calls it — through the injectable {@link IncomeAssessor} seam, so the projection engine
 * can hand in the assessor owned by its incomePlugin, the plugin that already owns WHEN this income
 * moves through the bank.
 */

import { isDefinedBenefitPension, definedBenefitTaxableIncome, definedBenefitTaxOffset, definedBenefitTaxedSource } from './definedBenefitPension.ts';
import { annuityTermsOf } from './annuityStrategies.ts';
import { COMPANY_TAX_RATE, OTHER_INCOME_TYPES } from './constants/ratesAndThresholds.ts';
import { getMinimumPensionDrawdownRateForAge } from './abpProjectionUtils.ts';
import { shouldSkipPersonalAssetProcessing } from './structureAttribution.ts';
import type { FactFind } from './factFindTypes.ts';
import type { StrategyOptions } from './strategyOptionsTypes.ts';

/** One named amount in a person's tax position — an income source, or a deduction. */
export interface TaxLineItem {
    name: string;
    amount: number;
}

/** One person's assessable position for the year, as the tax calculation consumes it. */
export interface PersonTaxPosition {
    /**
     * Always 0. Salary reaches the tax calculation as an `otherIncomes` entry, and so sits in
     * {@link investmentSources} with everything else; this field is what the tax breakdown's "Salary
     * Income" line renders, and it has always rendered zero. Kept because removing it would change that
     * breakdown.
     */
    salary: number;
    /** Every assessable income source, named for the breakdown. Share dividends appear GROSSED UP. */
    investmentSources: TaxLineItem[];
    /** Franking credits attached to those dividends — taxable income that is not money, offset against tax owed. */
    frankingCredits: number;
    /**
     * Non-refundable offsets that particular income earns — today the 10% offset on an untaxed-source
     * defined benefit pension (definedBenefitPension.ts). Applied after SAPTO and LITO, capped at the
     * tax still standing. Absent reads as nil.
     */
    taxOffsets?: number;
    /** Deductible expenses. `calculateFinancials` appends loan interest to this after the walk (its section 5). */
    deductionSources: TaxLineItem[];
}

/** The already-resolved position the walk is run against. */
export interface IncomeAssessmentInput {
    /** Read for `assets`, `income.otherIncomes` and `home.ownership`. */
    factFind: FactFind;
    /** Read for the rent-the-home strategy's `rentHome`/`rentAmount`/`rentExpenses`. */
    strategyOptions: StrategyOptions;
    isPartnered: boolean;
    /** Ages decide an ABP's minimum drawdown, and whether its payment is taxable (tax-free from 60). */
    clientAge: number | null;
    partnerAge: number | null;
    /** The trust/company apportionment, or null. Its tax lines and cash are merged in, not recomputed. */
    structureAttribution: any;
}

/** What the client earns this year, split into what is taxed and what is banked. */
export interface IncomeAssessment {
    /**
     * The two people's tax positions. Returned as live objects because `calculateFinancials` appends its
     * loan-interest deductions to them after this runs (see its section 5) before striking the tax.
     */
    clientTaxable: PersonTaxPosition;
    partnerTaxable: PersonTaxPosition;
    /** Cash from investments: interest, non-reinvested dividends, rent, annuity and term-deposit income. */
    investmentIncomeCash: number;
    /**
     * {@link investmentIncomeCash} split by WHAT KIND of investment produced it. Additive only — the
     * four together equal `investmentIncomeCash`, and nothing sums them again.
     *
     * They exist because one "Investment Income (Cash)" line on the Financial Snapshot lumped bank
     * interest, share dividends and property rent into a single figure. The total was right, but an
     * adviser reviewing it with a client could not see which asset produced what, and "investment
     * income (cash)" reads like a category error next to a rental property (reported 2026-08-22).
     */
    investmentIncomeParts: InvestmentIncomeParts;
    /** Cash drawn from account-based pensions. Tracked apart from investments because it is reported separately. */
    abpIncomeCash: number;
    /** Cash from `income.otherIncomes` — every entry, assessable or not, salary included. */
    otherIncomeCash: number;
    /** The salary component of {@link otherIncomeCash}, surfaced so a consumer can pay it monthly. Additive only. */
    salaryIncomeCash: number;
    /**
     * Dividends that were REINVESTED, and so never reached the bank. NOT inside
     * {@link investmentIncomeCash} — this is money the client is taxed on but does not receive.
     *
     * Surfaced so the Financial Snapshot can SAY so. An adviser reviewing a file with $150,000 of
     * shares at 4% asked "where is the income from the shares?" (2026-08-22): the engine was right to
     * leave it out of cashflow, but silently omitting it looks identical to forgetting it.
     */
    reinvestedDividends: number;
    /** Gross investment-property rent, already inside {@link investmentIncomeCash}. Additive only. */
    investmentPropertyRentCash: number;
    /** Investment-property expenses, already inside {@link totalRentalExpenses}. Additive only. */
    investmentPropertyExpenseCash: number;
    /** All rental expenses for the year — investment properties, the rented family home, and entity-held property. */
    totalRentalExpenses: number;
}

/** {@link IncomeAssessment.investmentIncomeParts} — cash investment income by kind. */
export interface InvestmentIncomeParts {
    /** Bank accounts and term deposits. */
    interest: number;
    /** Dividends actually banked — a reinvested dividend is taxed but never received, so it is not here. */
    dividends: number;
    /** Rent from investment properties, and from the family home when the strategy rents it out. */
    rent: number;
    /** Annuity payments, funeral bonds and anything under the catch-all `other` asset type. */
    other: number;
    /**
     * The client's share of trust and company income.
     *
     * Its own kind rather than folded into interest or dividends, because `structureAttribution`
     * hands it over as a single apportioned figure with no breakdown behind it — a trust's bank
     * interest and share dividends arrive already summed. Choosing either label for it would be a
     * guess, and "your family trust" is what the client recognises anyway.
     */
    structures: number;
}

/**
 * Assesses a client's taxable and cash income. Injected into `calculateFinancials` so a caller can own
 * the rules — see {@link calculateIncomeAssessment}, the default, and the projection engine's
 * incomePlugin.
 */
export type IncomeAssessor = (input: IncomeAssessmentInput) => IncomeAssessment;

/** An empty tax position, before any source is attributed to it. */
function emptyTaxPosition(): PersonTaxPosition {
    return { salary: 0, investmentSources: [], frankingCredits: 0, deductionSources: [] };
}

/**
 * Walks a client's bank accounts, income streams and then every asset, recording what each earns for tax
 * and what it pays in cash.
 *
 * Attribution is the same throughout: `'client'` and `'partner'` amounts go wholly to that person,
 * anything else is halved — and for a single client the other half simply goes unclaimed, which is what
 * makes a "joint" asset on a single-person advice assess only half.
 */
export const calculateIncomeAssessment: IncomeAssessor = ({
    factFind,
    strategyOptions,
    isPartnered,
    clientAge,
    partnerAge,
    structureAttribution,
}: IncomeAssessmentInput): IncomeAssessment => {
    const { assets, income = {} } = factFind;

    const clientTaxable = emptyTaxPosition();
    const partnerTaxable = emptyTaxPosition();

    let investmentIncomeCash = 0;
    // The same money as `investmentIncomeCash`, kept apart by kind so it can be REPORTED by kind.
    const investmentIncomeParts: InvestmentIncomeParts = { interest: 0, dividends: 0, rent: 0, other: 0, structures: 0 };
    let abpIncomeCash = 0;
    let otherIncomeCash = 0;
    let salaryIncomeCash = 0;
    let reinvestedDividends = 0;
    let investmentPropertyRentCash = 0;
    let investmentPropertyExpenseCash = 0;
    let totalRentalExpenses = 0;

    const bankAccounts = assets?.bankAccounts || [];
    bankAccounts.forEach((acc) => {
        if (shouldSkipPersonalAssetProcessing(acc.ownership)) return;
        const value = acc.value || 0;
        const rate = acc.incomeRate || 0;
        const interest = (value * rate) / 100;
        investmentIncomeCash += interest;
        investmentIncomeParts.interest += interest;
        if (acc.ownership === 'client') {
            clientTaxable.investmentSources.push({ name: acc.description || 'Bank Interest', amount: interest });
        } else if (acc.ownership === 'partner') {
            partnerTaxable.investmentSources.push({ name: acc.description || 'Bank Interest', amount: interest });
        } else if (acc.ownership === 'joint') {
            clientTaxable.investmentSources.push({ name: (acc.description || 'Bank Interest') + ' (50%)', amount: interest / 2 });
            if (isPartnered) partnerTaxable.investmentSources.push({ name: (acc.description || 'Bank Interest') + ' (50%)', amount: interest / 2 });
        }
    });

    if (strategyOptions.rentHome) {
        const annualRent = (strategyOptions.rentAmount || 0) * 52;
        const annualExpenses = strategyOptions.rentExpenses || 0;

        investmentIncomeCash += annualRent;
        investmentIncomeParts.rent += annualRent;
        totalRentalExpenses += annualExpenses;

        const homeOwnership = factFind.home?.ownership || 'joint';
        if (homeOwnership === 'client') {
            clientTaxable.investmentSources.push({ name: 'Rental Income - Family Home', amount: annualRent });
            clientTaxable.deductionSources.push({ name: 'Rental Expenses - Family Home', amount: annualExpenses });
        } else if (homeOwnership === 'partner') {
            if (isPartnered) {
                partnerTaxable.investmentSources.push({ name: 'Rental Income - Family Home', amount: annualRent });
                partnerTaxable.deductionSources.push({ name: 'Rental Expenses - Family Home', amount: annualExpenses });
            } else {
                clientTaxable.investmentSources.push({ name: 'Rental Income - Family Home', amount: annualRent });
                clientTaxable.deductionSources.push({ name: 'Rental Expenses - Family Home', amount: annualExpenses });
            }
        } else {
            clientTaxable.investmentSources.push({ name: 'Rental Income - Family Home (50%)', amount: annualRent / 2 });
            clientTaxable.deductionSources.push({ name: 'Rental Expenses - Family Home (50%)', amount: annualExpenses / 2 });
            if (isPartnered) {
                partnerTaxable.investmentSources.push({ name: 'Rental Income - Family Home (50%)', amount: annualRent / 2 });
                partnerTaxable.deductionSources.push({ name: 'Rental Expenses - Family Home (50%)', amount: annualExpenses / 2 });
            }
        }
    }

    // Process 'Other Incomes' for tax and cash
    (income.otherIncomes || []).forEach((inc) => {
        const amount = inc.amount || 0;
        const incomeType = OTHER_INCOME_TYPES.find((t) => t.id === inc.type);
        otherIncomeCash += amount;
        if (inc.type === 'salary') salaryIncomeCash += amount;

        // A defined benefit pension (definedBenefitPension.ts, 15 Oct 2026): a taxed source is tax-free
        // from 60 up to the defined benefit income cap, half of the excess assessable; an untaxed source
        // (CSS, PSS, the military and state schemes) is assessable at marginal rates with a 10% offset;
        // under 60 the taxable part is assessable either way.
        if (isDefinedBenefitPension(inc)) {
            const ownerAge = inc.ownership === 'partner' ? partnerAge : clientAge;
            const taxable = definedBenefitTaxableIncome(inc, ownerAge);
            const position = inc.ownership === 'partner' && isPartnered ? partnerTaxable : clientTaxable;
            if (taxable > 0) {
                const over60 = ownerAge != null && ownerAge >= 60;
                const note = !over60 ? '' : definedBenefitTaxedSource(inc) ? ' (half of the part above the income cap)' : ' (untaxed source)';
                position.investmentSources.push({ name: `${inc.description || incomeType!.name}${note}`, amount: taxable });
            }
            const offset = definedBenefitTaxOffset(inc, ownerAge);
            if (offset > 0) position.taxOffsets = (position.taxOffsets ?? 0) + offset;
            return;
        }

        let isTaxable = incomeType?.taxable;
        if (incomeType?.isCustom) {
            isTaxable = inc.isTaxable;
        }

        if (isTaxable) {
            if (inc.ownership === 'client') {
                clientTaxable.investmentSources.push({ name: inc.description || incomeType!.name, amount });
            } else if (inc.ownership === 'partner') {
                partnerTaxable.investmentSources.push({ name: inc.description || incomeType!.name, amount });
            } else {
                clientTaxable.investmentSources.push({ name: `${inc.description || incomeType!.name} (50%)`, amount: amount / 2 });
                if (isPartnered) partnerTaxable.investmentSources.push({ name: `${inc.description || incomeType!.name} (50%)`, amount: amount / 2 });
            }
        }
    });

    Object.values(assets || {}).forEach((assetList) => {
        (assetList || []).forEach((asset) => {
            if (shouldSkipPersonalAssetProcessing(asset.ownership)) return;

            const value = asset.value || 0;
            let currentCashIncome: number;
            let currentTaxable = 0;
            let currentFranking = 0;
            let currentDeductions = 0;
            // The `''` is unreachable: every push below is guarded by `currentTaxable`/`currentDeductions`
            // being positive, which only a matched asset-type branch can make true — so `asset.type` is
            // set wherever this is read. It is here because `FactFindAsset.type` is optional.
            const incomeSourceName = asset.description || asset.type || '';

            if (asset.type === 'shares') {
                const dividend = value * ((asset.incomeRate || 0) / 100);
                const frankingPercent = (asset.frankingCredit || 0) / 100;
                const frankedAmount = dividend * frankingPercent;
                const frankingCredit = frankedAmount * (COMPANY_TAX_RATE / (1 - COMPANY_TAX_RATE));

                currentCashIncome = dividend;
                currentTaxable = dividend + frankingCredit;
                currentFranking = frankingCredit;

                // A reinvested dividend is taxed but never banked.
                if (asset.reinvest !== true) {
                    investmentIncomeCash += currentCashIncome;
                    investmentIncomeParts.dividends += currentCashIncome;
                } else {
                    reinvestedDividends += currentCashIncome;
                }
            } else if (asset.type === 'investmentProperty') {
                const rent = asset.rent || 0;
                const expenses = asset.expenses || 0;
                currentCashIncome = rent;
                currentTaxable = rent;
                currentDeductions = expenses;
                totalRentalExpenses += expenses;
                investmentIncomeCash += currentCashIncome;
                investmentIncomeParts.rent += rent;
                investmentPropertyRentCash += rent;
                investmentPropertyExpenseCash += expenses;
            } else if (asset.type === 'accountBasedPensions') {
                let ownerAge: number | null = null;
                if (asset.ownership === 'client') {
                    ownerAge = clientAge;
                } else if (asset.ownership === 'partner') {
                    ownerAge = partnerAge;
                }

                // A pension must pay at least the legislated minimum for its owner's age. No resolvable
                // age means no minimum to impose, so the client's own nomination stands.
                const minDrawdownRate = ownerAge ? getMinimumPensionDrawdownRateForAge(ownerAge) : 0;
                const minDrawdownAmount = value * minDrawdownRate;
                const originalUserEnteredPayment = asset.income || 0;

                // An exhausted pension pays nothing, whatever was nominated against it.
                const userEnteredPayment = value > 0 ? originalUserEnteredPayment : 0;

                currentCashIncome = Math.max(userEnteredPayment, minDrawdownAmount);
                abpIncomeCash += currentCashIncome;

                // Tax-free from 60. Unresolvable ownership (a 'joint' ABP) stays taxable.
                let isTaxable = true;
                if (asset.ownership === 'client' && clientAge && clientAge >= 60) {
                    isTaxable = false;
                } else if (asset.ownership === 'partner' && partnerAge && partnerAge >= 60) {
                    isTaxable = false;
                }
                currentTaxable = isTaxable ? currentCashIncome : 0;
            } else if (asset.type && ['annuities', 'termDeposits', 'loansToOthers', 'other'].includes(asset.type)) {
                if (asset.type === 'annuities') {
                    // `annualPayment` from a strategy, `income` from the profile — and nil while a
                    // deferred annuity is deferred or once a fixed term has ended (annuityTermsOf).
                    // TAXABLE is the payment less its deductible amount (the tax-free return of
                    // capital: ITAA 1997 s 27H), and nil where the annuity was bought with super
                    // money and the owner is 60 or over. Until 17 Sep 2026 the Centrelink 60%
                    // figure stood in for the taxable amount on a strategy-built annuity and a
                    // profile-entered one was not taxed (or paid) at all.
                    const annuityOwnerAge = asset.ownership === 'partner' ? partnerAge : clientAge;
                    const annuityTerms = annuityTermsOf(asset, new Date(), annuityOwnerAge ?? null);
                    currentCashIncome = annuityTerms.annualPayment;
                    const superMoneyTaxFree = String(asset.fundingSource || '') === 'super' && (annuityOwnerAge ?? 0) >= 60;
                    currentTaxable = superMoneyTaxFree ? 0 : Math.max(0, currentCashIncome - annuityTerms.deductionAmount);
                } else {
                    let rate = 0;
                    // A loan to someone else pays the interest agreed on it, like a term deposit.
                    if ((asset.type === 'termDeposits' || asset.type === 'loansToOthers') && asset.incomeRate) {
                        rate = (asset.incomeRate || 0) / 100;
                    } else if (asset.income) {
                        rate = value > 0 ? asset.income / value : 0;
                    }
                    currentCashIncome = value * rate;
                    currentTaxable = currentCashIncome;
                }
                investmentIncomeCash += currentCashIncome;
                // A term deposit pays interest; an annuity or an `other` asset does not fit any of the
                // named kinds, so it lands in `other` rather than being mislabelled.
                if (asset.type === 'termDeposits' || asset.type === 'loansToOthers') investmentIncomeParts.interest += currentCashIncome;
                else investmentIncomeParts.other += currentCashIncome;
            }

            if (asset.ownership === 'client') {
                if (currentTaxable > 0) clientTaxable.investmentSources.push({ name: incomeSourceName, amount: currentTaxable });
                clientTaxable.frankingCredits += currentFranking;
                if (currentDeductions > 0) clientTaxable.deductionSources.push({ name: `${incomeSourceName} Expenses`, amount: currentDeductions });
            } else if (asset.ownership === 'partner') {
                if (currentTaxable > 0) partnerTaxable.investmentSources.push({ name: incomeSourceName, amount: currentTaxable });
                partnerTaxable.frankingCredits += currentFranking;
                if (currentDeductions > 0) partnerTaxable.deductionSources.push({ name: `${incomeSourceName} Expenses`, amount: currentDeductions });
            } else if (asset.ownership === 'joint') {
                if (currentTaxable > 0) clientTaxable.investmentSources.push({ name: `${incomeSourceName} (50%)`, amount: currentTaxable / 2 });
                clientTaxable.frankingCredits += currentFranking / 2;
                if (currentDeductions > 0) clientTaxable.deductionSources.push({ name: `${incomeSourceName} Expenses (50%)`, amount: currentDeductions / 2 });
                if (isPartnered) {
                    if (currentTaxable > 0) partnerTaxable.investmentSources.push({ name: `${incomeSourceName} (50%)`, amount: currentTaxable / 2 });
                    partnerTaxable.frankingCredits += currentFranking / 2;
                    if (currentDeductions > 0) partnerTaxable.deductionSources.push({ name: `${incomeSourceName} Expenses (50%)`, amount: currentDeductions / 2 });
                }
            }
        });
    });

    if (structureAttribution) {
        investmentIncomeCash += structureAttribution.investmentIncomeCash;
        investmentIncomeParts.structures += structureAttribution.investmentIncomeCash;
        totalRentalExpenses += structureAttribution.rentalExpenses;
        structureAttribution.clientTax.investmentSources.forEach((source: any) => {
            clientTaxable.investmentSources.push(source);
        });
        structureAttribution.clientTax.deductionSources.forEach((deduction: any) => {
            clientTaxable.deductionSources.push(deduction);
        });
        clientTaxable.frankingCredits += structureAttribution.clientTax.frankingCredits;
        if (isPartnered) {
            structureAttribution.partnerTax.investmentSources.forEach((source: any) => {
                partnerTaxable.investmentSources.push(source);
            });
            structureAttribution.partnerTax.deductionSources.forEach((deduction: any) => {
                partnerTaxable.deductionSources.push(deduction);
            });
            partnerTaxable.frankingCredits += structureAttribution.partnerTax.frankingCredits;
        }
    }

    return {
        clientTaxable,
        partnerTaxable,
        investmentIncomeCash,
        investmentIncomeParts,
        abpIncomeCash,
        otherIncomeCash,
        salaryIncomeCash,
        reinvestedDividends,
        investmentPropertyRentCash,
        investmentPropertyExpenseCash,
        totalRentalExpenses,
    };
};
